<template>
  <div class="licensee-audit">

    <p class="licensee-audit__intro">
      Licensees with billing configuration issues won't bill correctly under the new ledger — resolve each issue to reconcile.
    </p>

    <el-alert
      v-if="resuming"
      type="info"
      :closable="false"
      show-icon
      title=""
      description="Fees are recalculating in the background — this may take a moment." />

    <!-- Issue filter chips -->
    <div class="licensee-audit__chips">
      <el-radio-group v-model="activeIssue" size="small">
        <el-radio-button label="all">
          All ({{ counts.all || 0 }})
        </el-radio-button>
        <template v-for="issue in visibleIssueChips">
          <el-radio-button :key="issue.slug" :label="issue.slug">
            <i :class="issue.icon"></i>
            {{ issue.label }} ({{ counts[issue.slug] || 0 }})
          </el-radio-button>
        </template>
      </el-radio-group>
    </div>

    <xolas-table
      :key="activeIssue"
      url="/licensees/audit"
      table-ref="audittable"
      track-by="licensee_id"
      title="Billing Audit"
      :fields="fields"
      :params="tableParams"
      :show-search="true"
      search-placeholder="Search by organisation name…">

      <template #site="props">
        <div class="audit-site-cell">
          <span class="audit-site-cell__id">{{ props.rowData.site && props.rowData.site.siteId }}</span>
          <span class="audit-site-cell__name">{{ props.rowData.site && props.rowData.site.siteName }}</span>
        </div>
      </template>

      <template #issues="props">
        <template v-if="props.rowData.issues && props.rowData.issues.length">
          <el-tooltip
            v-for="(issue, idx) in props.rowData.issues"
            :key="idx"
            :content="issue.detail || issueLabel(issue.type)"
            placement="top">
            <el-tag
              :type="issue.severity === 'warning' ? 'warning' : 'danger'"
              size="small"
              effect="light"
              class="audit-issue-tag">
              <i :class="issue.severity === 'warning' ? 'fa fa-exclamation-triangle' : 'fa fa-exclamation-circle'"></i>
              {{ issueLabel(issue.type) }}
            </el-tag>
          </el-tooltip>
        </template>
        <el-tag v-else type="danger" size="small" effect="light">
          <i class="fa fa-tag"></i> No rate
        </el-tag>
      </template>

      <template #reconcile="props">
        <audit-reconcile-cell
          :row="props.rowData"
          :read-only="!canReconcile"
          @resolved="onResolved" />
      </template>

      <template #empty>
        <div class="audit-empty">
          <i class="fa fa-check-circle audit-empty__icon" />
          <p class="audit-empty__text">No licensees with this issue — all clear 🎉</p>
          <p class="audit-empty__muted">Nothing to reconcile right now.</p>
        </div>
      </template>

    </xolas-table>

  </div>
</template>

<script>

import axios from '@/lib/axiosClient'
import PostMixins from '@/mixins/PostMixins'
import NumberMixins from '@/mixins/NumberMixins'
import DateMixins from '@/mixins/DateMixins'

import XolasTable from '@/components/atoms/tables/XolasTable'
import AuditReconcileCell from './AuditReconcileCell'

// Slug → display label map (SPA-side; severity comes from the row's issue.severity).
var ISSUE_LABEL_MAP = {
  no_rate: 'No rate',
  no_start_anchor: 'No start date',
  no_billing_start: 'No billing start',
  expired_agreement: 'Expired',
  override_without_rate: 'Override, no rate',
  empty_override: 'Empty override',
  orphan_version_pin: 'Stale version pin',
  terminated_no_end: 'Terminated, no end',
  first_sharer_only_violation: 'First-sharer conflict',
  ambiguous_billing_mode: 'Ambiguous billing',
  expiring_soon: 'Expiring soon',
  no_structural_match: 'No structural match',
  missing_structure_height: 'No structure height',
  duplicate_billing_no: 'Duplicate sharer #'
}

// Ordered list for filter chips: errors first, then warnings.
var ISSUE_CHIP_ORDER = [
  { slug: 'no_rate', label: 'No rate', severity: 'error', icon: 'fa fa-exclamation-circle' },
  { slug: 'no_start_anchor', label: 'No start date', severity: 'error', icon: 'fa fa-exclamation-circle' },
  { slug: 'no_billing_start', label: 'No billing start', severity: 'error', icon: 'fa fa-exclamation-circle' },
  { slug: 'expired_agreement', label: 'Expired', severity: 'error', icon: 'fa fa-exclamation-circle' },
  { slug: 'override_without_rate', label: 'Override, no rate', severity: 'error', icon: 'fa fa-exclamation-circle' },
  { slug: 'empty_override', label: 'Empty override', severity: 'error', icon: 'fa fa-exclamation-circle' },
  { slug: 'orphan_version_pin', label: 'Stale version pin', severity: 'error', icon: 'fa fa-exclamation-circle' },
  { slug: 'terminated_no_end', label: 'Terminated, no end', severity: 'error', icon: 'fa fa-exclamation-circle' },
  { slug: 'first_sharer_only_violation', label: 'First-sharer conflict', severity: 'error', icon: 'fa fa-exclamation-circle' },
  { slug: 'ambiguous_billing_mode', label: 'Ambiguous billing', severity: 'error', icon: 'fa fa-exclamation-circle' },
  { slug: 'expiring_soon', label: 'Expiring soon', severity: 'warning', icon: 'fa fa-exclamation-triangle' },
  { slug: 'no_structural_match', label: 'No structural match', severity: 'warning', icon: 'fa fa-exclamation-triangle' },
  { slug: 'missing_structure_height', label: 'No structure height', severity: 'warning', icon: 'fa fa-exclamation-triangle' },
  { slug: 'duplicate_billing_no', label: 'Duplicate sharer #', severity: 'warning', icon: 'fa fa-exclamation-triangle' }
]

export default {
  name: 'licensee-audit',

  mixins: [PostMixins, NumberMixins, DateMixins],

  components: {
    XolasTable,
    AuditReconcileCell
  },

  computed: {
    canReconcile: function () {
      return this.isManager('finance')
    },

    tableParams: function () {
      return { issue: this.activeIssue, within: 60 }
    },

    // Only show chips for issue types that have count > 0.
    visibleIssueChips: function () {
      var counts = this.counts
      return ISSUE_CHIP_ORDER.filter(function (chip) {
        return counts[chip.slug] && counts[chip.slug] > 0
      })
    }
  },

  methods: {
    // 2-decimal currency for the legacy-bill column (formatMoney defaults to 4dp).
    formatLegacyBill: function (value) {
      if (value === null || value === undefined) return '—'
      return this.formatMoney(value, 2)
    },

    // Resolve a display label from the slug→label map.
    issueLabel: function (slug) {
      return ISSUE_LABEL_MAP[slug] || slug
    },

    // Fetch the summary chip counts from /licensees/audit/summary.
    fetchSummary: function () {
      var self = this
      axios.get(this.getEndpoint('/licensees/audit/summary'), { params: { within: 60 } })
        .then(function (response) {
          var data = response.data
          if (data && data.counts) {
            self.counts = data.counts
          }
        })
        .catch(function (err) {
          console.error('[LicenseeAudit] summary fetch failed', err)
        })
    },

    onResolved: function () {
      this.resuming = true
      // The audittable-refresh-table event is fired by saveForm (via eventTableRef
      // in PostMixins) when the cell completes. The row drops on re-fetch.
      // Clear the transient alert after a short delay.
      var self = this
      setTimeout(function () {
        self.resuming = false
      }, 5000)
      // Re-fetch summary counts so chips stay live after a resolve.
      this.fetchSummary()
    },

  },

  mounted: function () {
    this.fetchSummary()
  },

  data: function () {
    return {
      resuming: false,
      activeIssue: 'all',
      counts: {},
      fields: [
        {
          name: '__sequence',
          title: '#',
          width: 56,
          align: 'center'
        },
        {
          name: 'organisation.name',
          title: 'Sharer',
          minWidth: 160
        },
        {
          name: '__slot:site',
          title: 'Site',
          minWidth: 150
        },
        {
          name: 'sharerNo',
          title: 'Sharer #',
          width: 96,
          align: 'center',
          titleClass: 'hidden-sm-and-down',
          dataClass: 'hidden-sm-and-down'
        },
        {
          name: 'started',
          title: 'Started',
          width: 120,
          callback: this.formatDate,
          titleClass: 'hidden-sm-and-down',
          dataClass: 'hidden-sm-and-down'
        },
        {
          name: 'expiry',
          title: 'Expiry',
          width: 120,
          callback: this.formatDate,
          titleClass: 'hidden-sm-and-down',
          dataClass: 'hidden-sm-and-down'
        },
        {
          name: 'currentLegacyBill',
          title: 'Legacy bill',
          width: 130,
          align: 'right',
          callback: this.formatLegacyBill
        },
        {
          name: '__slot:issues',
          title: 'Issues',
          minWidth: 180
        },
        {
          name: '__slot:reconcile',
          title: 'Reconcile',
          width: 200,
          align: 'center'
        }
      ]
    }
  }
}
</script>

<style scoped>
.licensee-audit__intro {
  color: #606266;
  margin-bottom: 12px;
}

.licensee-audit__chips {
  margin-bottom: 16px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.audit-issue-tag {
  margin-right: 4px;
  margin-bottom: 2px;
}

.audit-site-cell__id {
  display: block;
  font-weight: 600;
}

.audit-site-cell__name {
  display: block;
  color: #909399;
  font-size: 12px;
}

.audit-empty {
  padding: 32px 0;
  text-align: center;
}

.audit-empty__icon {
  font-size: 48px;
  color: #67c23a;
  display: block;
  margin-bottom: 12px;
}

.audit-empty__text {
  font-size: 16px;
  margin-bottom: 4px;
}

.audit-empty__muted {
  color: #909399;
  font-size: 13px;
}
</style>
